import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  JaneGoldTag,
  SpecialTag,
  getMaxCartQuantity,
  getSpecialTagLabel,
  productPhotoMetadata,
} from '@iheartjane/menu';
import {
  Box,
  Card,
  ConditionalWrapper,
  Flex,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useCallback } from 'react';

import { useShouldShowGold } from '@jane/shared/hooks';
import { canWeightBeAddedToCart } from '@jane/shared/util';

import { DisplayMode, useProductCardContext } from '../productCardProvider';
import { MenuBasicProductInfo, MenuProductPrices } from './menuProductInfo';
import { initialCountForProduct } from './utils/initialCountForProduct';
import type { LinkProps } from './utils/productCardHelper';
import { ProductDetailPageLink } from './utils/productCardHelper';

import styles from './legacyMenuProductCard.module.css';

interface Props {
  height?: number | string;
  maxWeightOption?: number | null;
  onAddToCartPressed: (qty?: number) => void;
  shoppingDisabled: boolean;
}

const UpdateCount = ({ count }: { count: number }) => (
  <Flex
    className={styles['count']}
    as="span"
    alignItems="center"
    justifyContent="center"
    background="primary"
    height={20}
    width={20}
    inline
    ml={8}
  >
    <Typography
      className={styles['countNumber']}
      color="text-inverse"
      variant="small-body-semibold"
    >
      {count}
    </Typography>
  </Flex>
);

export const MenuProductCardDefaultView = ({
  height,
  maxWeightOption,
  onAddToCartPressed,
  shoppingDisabled,
}: Props) => {
  const oneClickAddToBagEnabled = useFlag(FLAGS.c3OneClickAddToBag);
  const {
    appMode,
    brand,
    breadcrumbs,
    cartIsOpen,
    cartProduct,
    compareOverlay,
    currentSpecial,
    defaultWeight,
    disableInteraction,
    fromSpecialId,
    fromAllSpecials,
    hasNonStandardDefaultWeight,
    hideActions,
    janeGoldLabel,
    menuProduct: product,
    pdpPath,
    searchState,
    selectedWeight,
    setDisplayMode,
    showOnlyWeights,
    store,
  } = useProductCardContext();
  const isMobile = useMobileMediaQuery({});
  const { url } = productPhotoMetadata(product);
  const shouldShowGold = useShouldShowGold({ store });

  const count = initialCountForProduct(cartProduct);

  const specialTagLabel =
    store &&
    product &&
    getSpecialTagLabel({
      defaultWeight,
      product,
      store,
    });

  const specialApplies = specialTagLabel && currentSpecial;

  const sharedLinkProps = {
    appMode,
    brand,
    breadcrumbs,
    cartIsOpen,
    defaultWeight: hasNonStandardDefaultWeight ? defaultWeight : undefined,
    product,
    searchState,
    store,
    fromAllSpecials,
    fromSpecialId,
    pdpPath,
    bundleSpecialId:
      currentSpecial?.special_type === 'bundle' ? currentSpecial.id : undefined,
  } as Omit<LinkProps, 'children'>;

  const hasMultipleWeights =
    showOnlyWeights?.length !== 1 &&
    (product?.available_weights || []).length > 1;

  const { canAddMore } = canWeightBeAddedToCart(
    selectedWeight,
    cartProduct,
    count,
    maxWeightOption
  );
  const reachedMaxCartQuantity =
    canAddMore && product
      ? getMaxCartQuantity(product, selectedWeight) === count
      : true;

  const addToClickHandler = useCallback(() => {
    !disableInteraction &&
      !shoppingDisabled &&
      (oneClickAddToBagEnabled &&
      !!selectedWeight &&
      !hasMultipleWeights &&
      !reachedMaxCartQuantity
        ? onAddToCartPressed(count + 1)
        : setDisplayMode(DisplayMode.Edit));
  }, [
    count,
    disableInteraction,
    hasMultipleWeights,
    onAddToCartPressed,
    oneClickAddToBagEnabled,
    reachedMaxCartQuantity,
    shoppingDisabled,
    setDisplayMode,
    selectedWeight,
  ]);

  const janeGoldTagLabel = shouldShowGold ? janeGoldLabel : false;

  return (
    <Card className={styles['cardContent']} width="100%" height={height}>
      <Flex
        flexWrap="wrap"
        gap={4}
        justifyContent="flex-end"
        p={8}
        position="absolute"
        width="100%"
        zIndex="var(--layers-visible)"
        style={{ pointerEvents: 'none' }}
      >
        {janeGoldTagLabel && <JaneGoldTag label={janeGoldTagLabel} />}
        {specialApplies && <SpecialTag label={specialTagLabel} />}
      </Flex>
      <ConditionalWrapper
        condition={!disableInteraction}
        wrapper={(children) => (
          <ProductDetailPageLink aria-hidden tabIndex={-1} {...sharedLinkProps}>
            {children}
          </ProductDetailPageLink>
        )}
      >
        <ConditionalWrapper
          condition={!!compareOverlay}
          wrapper={(children) => (
            <Box position="relative">
              {children}
              {compareOverlay}
            </Box>
          )}
        >
          <Card.Image
            alt={product?.name || ''}
            height={isMobile ? '150px' : '200px'}
            responsive
            sizes="card"
            src={url}
            data-testid="menu-product-card default-view"
          />
        </ConditionalWrapper>
      </ConditionalWrapper>
      <Card.Content>
        <Flex flexDirection="column" height="100%">
          <ConditionalWrapper
            condition={!disableInteraction}
            wrapper={(children) => (
              <ProductDetailPageLink
                aria-label={`Details for ${product?.name}`}
                {...sharedLinkProps}
              >
                {children}
              </ProductDetailPageLink>
            )}
          >
            <MenuBasicProductInfo selectedWeight={defaultWeight} />
          </ConditionalWrapper>
          <MenuProductPrices currentSpecial={currentSpecial} />
        </Flex>
      </Card.Content>
      {!hideActions && (
        <Card.Action
          data-testid="menu-product-card-cta"
          ariaLabel={
            shoppingDisabled
              ? 'shopping disabled'
              : (cartProduct || []).length > 0
                ? `update bag button for ${product?.name}`
                : `add to bag button for ${product?.name}`
          }
          disabled={shoppingDisabled}
          endIcon={
            !shoppingDisabled &&
            (cartProduct || []).length > 0 && <UpdateCount count={count} />
          }
          onClick={addToClickHandler}
          label={
            shoppingDisabled
              ? 'Shopping disabled'
              : oneClickAddToBagEnabled
                ? !hasMultipleWeights && !reachedMaxCartQuantity
                  ? 'Add to bag'
                  : (cartProduct || []).length > 0
                    ? 'Update'
                    : 'Select weight'
                : (cartProduct || []).length > 0
                  ? 'Update'
                  : hasMultipleWeights
                    ? 'Select weight'
                    : 'Add to bag'
          }
          variant="secondary"
        />
      )}
    </Card>
  );
};
